// Brainstorm mode — Document lens for a channel session.
//
// Each Ask in brainstorm mode does two things:
//   1. Updates a shared markdown whiteboard (session.document). The AI
//      reads the current whiteboard + the recent chat, and emits the
//      COMPLETE updated whiteboard wrapped in <whiteboard>...</whiteboard>
//      tags. Full-rewrite instead of diffs — universal across models,
//      no special tool-use surface needed.
//   2. Posts a one-line acknowledgment to the chat thread (the text
//      OUTSIDE the whiteboard tags). Keeps the chat surface light;
//      detail lives in the document.
//
// Mosh Pit is intentionally bypassed here: a 3-candidate pick doesn't
// fit "the AI rewrites the shared whiteboard" — each candidate would
// produce a different document and there's no clean way to merge.
// Single-model only, BabyAI primary, Anthropic fallback.

import { callBabyAI, callBabyAIStream, type BabyAIMessage } from "./babyai-client";
import { getClient, DEFAULT_MODEL as DEFAULT_ANTHROPIC_MODEL } from "./anthropic-client";
import { useKeyStore } from "../store/keyStore";
import { useChannelKeyStore } from "../store/channelKeyStore";
import type { Session } from "./session";
import { buildAttachedDocsBlock } from "./chat-send";

export interface BrainstormResult {
  /** The full new whiteboard markdown (replaces session.document). */
  document: string;
  /** Short acknowledgment to post into the chat as an assistant turn. */
  ack: string;
  /** Model that actually responded (for the message metadata). */
  model: string;
  /** Token usage from the underlying call, if the provider reported
   *  it. Used by the toolbar's cost meter. */
  usage?: { prompt: number; completion: number };
}

export interface BrainstormCandidate {
  /** Organizing strategy this candidate used (label for the tab). */
  strategy: string;
  /** Short description of the strategy (tab tooltip). */
  hint: string;
  /** The full proposed whiteboard markdown. */
  document: string;
  /** Brief ack the model emitted for this strategy. */
  ack: string;
}

export interface BrainstormMoshPitResult {
  /** Three (or fewer, if some failed) candidate rewrites — one per
   *  strategy. The user picks which one becomes the new document. */
  candidates: BrainstormCandidate[];
  /** Model id that produced these (all candidates share the model). */
  model: string;
  /** Summed token usage across all successful candidates — Mosh-Pit
   *  spends roughly 3x a single call, so the meter reflects that. */
  usage?: { prompt: number; completion: number };
}

export interface BrainstormOptions {
  session: Session;
  /** Fires as the whiteboard streams in — receives the accumulated
   *  whiteboard text so far, suitable for live-rendering the panel.
   *  Each call is the full current snapshot, not a delta. */
  onDocumentProgress?: (snapshot: string) => void;
}

// The three strategies for Mosh-Pit brainstorm. Each is a one-line
// addition to the system prompt that biases the AI's reorganization
// toward a particular axis. Users pick the rendering that lands best.
export const BRAINSTORM_STRATEGIES: Array<{ label: string; hint: string; instruction: string }> = [
  {
    label: "By theme",
    hint: "cluster ideas into thematic groups",
    instruction: "Organize the whiteboard primarily by THEME — group related ideas under H2 thematic headings, even if that means splitting one person's contribution across sections.",
  },
  {
    label: "By contributor",
    hint: "credit each person's ideas separately",
    instruction: "Organize the whiteboard primarily by CONTRIBUTOR — give each person an H2 section listing their ideas, then a final H2 \"Synthesis\" pulling threads together.",
  },
  {
    label: "By maturity",
    hint: "early thoughts vs. crystallized decisions",
    instruction: "Organize the whiteboard primarily by MATURITY of the idea — H2 sections \"Decided\", \"Converging\", \"Exploring\", \"Open questions\" — and sort each contribution into the right one.",
  },
];

/** Pull the distinct user authors who've spoken in the session. Order:
 *  most recently active first, capped at 8 so the system prompt
 *  doesn't bloat in a long channel. Used to tell the AI who counts as
 *  "the team" right now, so attributions stay accurate even when older
 *  messages reference people who've since left or renamed. */
function activeRoster(session: Session): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  // Walk newest-to-oldest so recent activity wins ordering.
  for (let i = session.messages.length - 1; i >= 0; i--) {
    const m = session.messages[i];
    if (m.role !== "user") continue;
    const name = (m.author ?? "").trim();
    if (!name || seen.has(name)) continue;
    seen.add(name);
    out.push(name);
    if (out.length >= 8) break;
  }
  return out;
}

/** When the whiteboard crosses this byte length, the system prompt
 *  gets a "compress as you rewrite" instruction. ~6000 chars is
 *  roughly 1500 tokens of input on every Ask — at that scale, a
 *  silent self-compress beats letting the doc grow unbounded. */
const COMPRESS_THRESHOLD = 6000;

function buildSystemPrompt(
  currentDocument: string,
  strategyInstruction?: string,
  roster: string[] = [],
): string {
  const doc = currentDocument.trim();
  const docBlock = doc.length > 0
    ? `Current whiteboard:\n\n<whiteboard>\n${doc}\n</whiteboard>`
    : `The whiteboard is empty — this is the first contribution. Start it.`;
  const rosterLine = roster.length > 0
    ? `Active contributors in this brainstorm (most recent first): ${roster.join(", ")}. Use these names verbatim in [bracket] attributions.`
    : "";
  const compressLine = doc.length > COMPRESS_THRESHOLD
    ? "The whiteboard is getting long. As part of this rewrite, compress similar bullets, merge sections that overlap, prune dead branches, and aim for ~25% shorter while preserving every meaningful idea and attribution. Do NOT drop attributions or specific contributions — compress phrasing and structure, not substance."
    : "";

  const lines = [
    "You are the facilitator of a team brainstorm in a shared chat channel.",
    "Multiple humans contribute ideas. Your job each turn:",
    "",
    "1. Read the latest user message in context of the running whiteboard.",
    "2. Update the whiteboard to incorporate their idea. Organize by theme,",
    "   deduplicate, surface tensions, and add your own riffs when they",
    "   help the group think. Keep it readable: H2 sections, bullets,",
    "   names in [brackets] when an idea came from a specific person.",
    "3. Emit your response in this exact shape:",
    "",
    "<whiteboard>",
    "... the COMPLETE updated whiteboard markdown ...",
    "</whiteboard>",
    "Then a single short line of acknowledgment for the chat — a diff",
    "teaser, not a summary. Lead with a change verb (added / moved /",
    "merged / expanded / reordered / split / pruned), name the specific",
    "section affected, and credit the contributor in [brackets] when",
    "their idea drove the change. Examples:",
    "",
    "  added [alice]'s point on cost ceilings under Risks",
    "  merged [bob] and [claire]'s overlapping themes into one bullet",
    "  reordered Pros to lead with the cheapest wins",
    "  expanded the user-journey Awareness stage with two sub-bullets",
    "",
    "Rules:",
    "- ALWAYS emit the full whiteboard inside the tags. Partial diffs are",
    "  not supported.",
    "- NEVER include the <whiteboard> tags in the chat ack line.",
    "- NEVER summarize the whole document in the ack — only what",
    "  changed this turn. One sentence, plain prose, no markdown",
    "  headings.",
    "",
  ];
  if (strategyInstruction) {
    lines.push("Strategy hint for this turn: " + strategyInstruction, "");
  }
  if (rosterLine) {
    lines.push(rosterLine, "");
  }
  if (compressLine) {
    lines.push(compressLine, "");
  }
  lines.push(docBlock);
  return lines.join("\n");
}

function parseResponse(text: string): { document: string | null; ack: string } {
  const m = text.match(/<whiteboard>\s*([\s\S]*?)\s*<\/whiteboard>/);
  if (!m) {
    // Model didn't emit the tag — treat the whole response as ack and
    // leave the document unchanged. UI can flag the miss.
    return { document: null, ack: text.trim() || "(no acknowledgment)" };
  }
  const document = m[1].trim();
  const ack = text.replace(m[0], "").trim();
  return { document, ack: ack || "(updated whiteboard)" };
}

const OPEN_TAG = "<whiteboard>";
const CLOSE_TAG = "</whiteboard>";

/** Stream filter that watches a token stream for <whiteboard>…
 *  </whiteboard> and fires onDocumentProgress with the accumulated
 *  inside-tag content. Tolerates tags split across token boundaries
 *  by holding back the last (max-tag-length - 1) chars until they
 *  resolve into a tag or get flushed as plain text. */
function makeStreamFilter(onDocumentProgress?: (snapshot: string) => void) {
  let buffer = "";
  let inDoc = false;
  let doc = "";

  function pump() {
    while (true) {
      if (!inDoc) {
        const i = buffer.indexOf(OPEN_TAG);
        if (i >= 0) {
          // Bytes before the tag are chat-ack text we don't surface
          // live (we post the ack once at end). Drop them from buffer.
          buffer = buffer.slice(i + OPEN_TAG.length);
          inDoc = true;
          continue;
        }
        // Hold back the last (OPEN_TAG.length - 1) chars in case
        // they begin a tag that finishes on the next chunk.
        const safe = Math.max(0, buffer.length - (OPEN_TAG.length - 1));
        buffer = buffer.slice(safe);
        return;
      }
      // inDoc
      const i = buffer.indexOf(CLOSE_TAG);
      if (i >= 0) {
        doc += buffer.slice(0, i);
        buffer = buffer.slice(i + CLOSE_TAG.length);
        inDoc = false;
        if (onDocumentProgress) onDocumentProgress(doc.trim());
        continue;
      }
      const safe = Math.max(0, buffer.length - (CLOSE_TAG.length - 1));
      if (safe > 0) {
        doc += buffer.slice(0, safe);
        buffer = buffer.slice(safe);
        if (onDocumentProgress) onDocumentProgress(doc.trim());
      }
      return;
    }
  }

  return {
    feed(delta: string) {
      buffer += delta;
      pump();
    },
    /** Flush any tail buffer that we held back during streaming. */
    end() {
      // If we're still inside a doc block at end of stream, treat
      // the remaining buffer as doc content (the model probably
      // truncated the close tag). The fallback parseResponse() also
      // catches a fully-missing close tag if it gets that far.
      if (inDoc && buffer.length > 0) {
        doc += buffer;
        buffer = "";
        if (onDocumentProgress) onDocumentProgress(doc.trim());
      }
    },
  };
}

/** Channel-attached key (decrypted) > personal key. Mirrors chat-send. */
function resolveKeys(channelId: string): { babyai: string | null; anthropic: string | null } {
  const channelKeys = useChannelKeyStore.getState().get(channelId);
  const personal = useKeyStore.getState();
  return {
    babyai: channelKeys?.babyai ?? personal.babyai,
    anthropic: channelKeys?.anthropic ?? personal.anthropic,
  };
}

export async function sendBrainstorm(opts: BrainstormOptions): Promise<BrainstormResult> {
  const keys = resolveKeys(opts.session.id);
  const usePublic = useKeyStore.getState().usePublic;
  const systemPrompt = buildSystemPrompt(opts.session.document ?? "", undefined, activeRoster(opts.session)) + buildAttachedDocsBlock(opts.session);

  // Include the recent chat thread as context. User messages get
  // prefixed with their author name so the AI can attribute ideas in
  // the whiteboard (e.g. "[alice]'s point on X").
  const history: BabyAIMessage[] = opts.session.messages
    .filter((m) => m.role === "user" || m.role === "assistant")
    .map((m) => ({
      role: m.role as "user" | "assistant",
      content: m.role === "user" && m.author
        ? `[${m.author}]: ${m.content}`
        : m.content,
    }));

  const wantBabyAI = opts.session.provider_preference !== "anthropic";
  const babyaiAvailable = !!keys.babyai || usePublic;

  if (wantBabyAI && babyaiAvailable) {
    const filter = makeStreamFilter(opts.onDocumentProgress);
    const result = await callBabyAIStream({
      modelId: "hf_qwen3_8b",
      messages: history,
      systemInstruction: systemPrompt,
      maxTokens: 2048,
      token: keys.babyai ?? undefined,
      onToken: (delta) => filter.feed(delta),
    });
    filter.end();
    const parsed = parseResponse(result.text);
    return {
      document: parsed.document ?? (opts.session.document ?? ""),
      ack: parsed.ack,
      model: result.model,
      usage: result.usage
        ? { prompt: result.usage.prompt_tokens ?? 0, completion: result.usage.completion_tokens ?? 0 }
        : undefined,
    };
  }

  if (keys.anthropic) {
    const client = await getClient(keys.anthropic);
    const filter = makeStreamFilter(opts.onDocumentProgress);
    let fullText = "";
    const stream = client.messages.stream({
      model: DEFAULT_ANTHROPIC_MODEL,
      max_tokens: 2048,
      system: systemPrompt,
      messages: history.map((m) => ({ role: m.role as "user" | "assistant", content: m.content })),
    });
    stream.on("text", (delta) => {
      fullText += delta;
      filter.feed(delta);
    });
    const final = await stream.finalMessage();
    filter.end();
    const parsed = parseResponse(fullText);
    return {
      document: parsed.document ?? (opts.session.document ?? ""),
      ack: parsed.ack,
      model: DEFAULT_ANTHROPIC_MODEL,
      usage: final?.usage
        ? { prompt: final.usage.input_tokens ?? 0, completion: final.usage.output_tokens ?? 0 }
        : undefined,
    };
  }

  throw new Error(
    "No BabyAI access. Add a key in the Keys panel, turn on \"Use public BabyAI\" there, " +
    "or unlock the channel's attached keys.",
  );
}

// ---------------------------------------------------------------------------
// Reframe — recast the existing whiteboard into a new structure without
// losing content. Used when the user picks a new starter frame mid-
// brainstorm. Streaming, single-strategy.
// ---------------------------------------------------------------------------

export interface ReframeOptions {
  session: Session;
  /** The target template (markdown body) the doc should fit into.
   *  Comes from a FRAMES entry in DocumentPanel. The current
   *  whiteboard content gets reorganized to live under whichever
   *  headings the template defines. */
  targetTemplate: string;
  /** Human-readable label of the target frame (for the chat ack). */
  targetLabel: string;
  onDocumentProgress?: (snapshot: string) => void;
}

function buildReframeSystemPrompt(currentDocument: string, targetTemplate: string, targetLabel: string): string {
  return [
    "You are reframing an existing brainstorm whiteboard into a new",
    "structure. DO NOT drop, dilute, or summarize away the existing",
    "content — every meaningful idea, attribution, and section detail",
    "must end up somewhere in the new shape. Reorganize, regroup, and",
    "rename headings to match the target frame; merge what genuinely",
    "duplicates; add new sub-bullets only if the structure demands a",
    "placeholder for content the existing doc doesn't have yet.",
    "",
    "Emit your response in this exact shape:",
    "",
    "<whiteboard>",
    "... the COMPLETE reframed whiteboard markdown ...",
    "</whiteboard>",
    "Then one short line of acknowledgment for the chat (e.g. \"reframed",
    `into ${targetLabel}; mapped existing points to the new sections\").`,
    "",
    "Rules:",
    "- ALWAYS emit the full whiteboard inside the tags.",
    "- NEVER include the <whiteboard> tags in the chat ack line.",
    "",
    "Target frame (use these headings; you may add to them):",
    "",
    "<targetframe>",
    targetTemplate,
    "</targetframe>",
    "",
    "Current whiteboard to reframe:",
    "",
    "<whiteboard>",
    currentDocument || "(empty)",
    "</whiteboard>",
  ].join("\n");
}

export async function reframeBrainstorm(opts: ReframeOptions): Promise<BrainstormResult> {
  const keys = resolveKeys(opts.session.id);
  const usePublic = useKeyStore.getState().usePublic;
  const wantBabyAI = opts.session.provider_preference !== "anthropic";
  const babyaiAvailable = !!keys.babyai || usePublic;
  const systemPrompt = buildReframeSystemPrompt(
    opts.session.document ?? "",
    opts.targetTemplate,
    opts.targetLabel,
  ) + buildAttachedDocsBlock(opts.session);

  if (wantBabyAI && babyaiAvailable) {
    const filter = makeStreamFilter(opts.onDocumentProgress);
    const result = await callBabyAIStream({
      modelId: "hf_qwen3_8b",
      messages: [{ role: "user", content: `Reframe into ${opts.targetLabel}.` }],
      systemInstruction: systemPrompt,
      maxTokens: 2048,
      token: keys.babyai ?? undefined,
      onToken: (delta) => filter.feed(delta),
    });
    filter.end();
    const parsed = parseResponse(result.text);
    return {
      document: parsed.document ?? (opts.session.document ?? ""),
      ack: parsed.ack || `reframed into ${opts.targetLabel}`,
      model: result.model,
    };
  }

  if (keys.anthropic) {
    const client = await getClient(keys.anthropic);
    const filter = makeStreamFilter(opts.onDocumentProgress);
    let fullText = "";
    const stream = client.messages.stream({
      model: DEFAULT_ANTHROPIC_MODEL,
      max_tokens: 2048,
      system: systemPrompt,
      messages: [{ role: "user", content: `Reframe into ${opts.targetLabel}.` }],
    });
    stream.on("text", (delta) => {
      fullText += delta;
      filter.feed(delta);
    });
    const final = await stream.finalMessage();
    filter.end();
    const parsed = parseResponse(fullText);
    return {
      document: parsed.document ?? (opts.session.document ?? ""),
      ack: parsed.ack || `reframed into ${opts.targetLabel}`,
      model: DEFAULT_ANTHROPIC_MODEL,
      usage: final?.usage
        ? { prompt: final.usage.input_tokens ?? 0, completion: final.usage.output_tokens ?? 0 }
        : undefined,
    };
  }

  throw new Error(
    "No BabyAI access. Add a key in the Keys panel, turn on \"Use public BabyAI\" there, " +
    "or unlock the channel's attached keys.",
  );
}

// ---------------------------------------------------------------------------
// Mosh-Pit brainstorm — 3 parallel rewrites, one per organizing strategy.
// Non-streaming (running 3 streams in parallel is messy UX); user picks a
// tab in the panel to commit the chosen rewrite to session.document.
// ---------------------------------------------------------------------------

export async function sendBrainstormMoshPit(opts: BrainstormOptions): Promise<BrainstormMoshPitResult> {
  const keys = resolveKeys(opts.session.id);
  const usePublic = useKeyStore.getState().usePublic;
  const wantBabyAI = opts.session.provider_preference !== "anthropic";
  const babyaiAvailable = !!keys.babyai || usePublic;

  const history: BabyAIMessage[] = opts.session.messages
    .filter((m) => m.role === "user" || m.role === "assistant")
    .map((m) => ({
      role: m.role as "user" | "assistant",
      content: m.role === "user" && m.author
        ? `[${m.author}]: ${m.content}`
        : m.content,
    }));
  const roster = activeRoster(opts.session);

  if (wantBabyAI && babyaiAvailable) {
    // Three parallel calls, one per strategy. Promise.allSettled so
    // one failing strategy doesn't take the whole panel down.
    const settled = await Promise.allSettled(
      BRAINSTORM_STRATEGIES.map((strat) =>
        callBabyAI({
          modelId: "hf_qwen3_8b",
          messages: history,
          systemInstruction: buildSystemPrompt(opts.session.document ?? "", strat.instruction, roster) + buildAttachedDocsBlock(opts.session),
          maxTokens: 2048,
          token: keys.babyai ?? undefined,
        }).then((result) => {
          const parsed = parseResponse(result.text);
          return {
            strategy: strat.label,
            hint: strat.hint,
            document: parsed.document ?? (opts.session.document ?? ""),
            ack: parsed.ack,
            model: result.model,
            usage: result.usage,
          };
        }),
      ),
    );
    const candidates: BrainstormCandidate[] = [];
    let model = "";
    let promptTok = 0;
    let completionTok = 0;
    for (const s of settled) {
      if (s.status === "fulfilled") {
        candidates.push({
          strategy: s.value.strategy,
          hint: s.value.hint,
          document: s.value.document,
          ack: s.value.ack,
        });
        model = s.value.model;
        if (s.value.usage) {
          promptTok += s.value.usage.prompt_tokens ?? 0;
          completionTok += s.value.usage.completion_tokens ?? 0;
        }
      } else {
        console.warn("[brainstorm.moshpit] strategy failed:", s.reason);
      }
    }
    if (candidates.length === 0) {
      throw new Error("All Mosh-Pit strategies failed — check the console for upstream errors.");
    }
    return {
      candidates,
      model,
      usage: (promptTok + completionTok) > 0
        ? { prompt: promptTok, completion: completionTok }
        : undefined,
    };
  }

  throw new Error(
    "Mosh-Pit brainstorm needs BabyAI access. Add a key or turn on \"Use public BabyAI\" — " +
    "the Anthropic fallback runs single-strategy only.",
  );
}
